import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import './UpdateValueLink.scss';
import {ListItemText, makeStyles} from '@material-ui/core';
import LinkDropdown from '../LinkDropDown/LinkDropdown';
import {CustomPopOverListItemType} from '../CustomPopOver/CustomPopOver';
import {APP_PRIMARY_COLOR} from 'src/style/variable';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';

export interface IUpdateValueOptionItem {
  label: string;
  // eslint-disable-next-line camelcase
  secondary_label?: string;
  value: string | number;
}
interface IUpdateValueLinkProps<T extends IUpdateValueOptionItem> {
  value: any;
  optionsList: T[];
  onSelectOption: (
    selectedItem: any,
    event?: React.MouseEvent<EventTarget>
  ) => void;
  updateValueLabel?: string;
  addValueLabel?: string;
  canUpdate?: boolean;
  getDefaultSelectedOption?: (selectedValue: any) => Promise<T | void>;
}

const useStyles = makeStyles(() => ({
  listItem: {
    marginBlock: 0,
    '& .MuiListItemText-primary': {
      overflow: 'hidden',
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontFamily: 'RobotoMedium'
    },
    '& .MuiListItemText-secondary': {
      fontSize: '0.9rem',
      marginTop: '0.2rem',
      overflow: 'hidden',
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    }
  },
  rootListItemPopOverLink: {
    margin: 0,
    minWidth: '10rem'
  },
  primaryListItemPopOverLink: {
    overflow: 'hidden',
    width: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: APP_PRIMARY_COLOR
  },
  secondaryListItemPopOverLink: {
    fontSize: '0.9rem',
    marginTop: '0.2rem',
    overflow: 'hidden',
    width: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  }
}));

const UpdateValueLink = <T extends IUpdateValueOptionItem>(
  props: IUpdateValueLinkProps<T>
): JSX.Element => {
  const {t} = useTranslation();
  const {
    value,
    optionsList,
    onSelectOption,
    addValueLabel = t('COMMON.ADD') as string,
    updateValueLabel = t('COMMON.CHANGE_TO') as string,
    canUpdate = true,
    getDefaultSelectedOption
  } = props;
  const classes = useStyles();
  const {enqueueSnackbar} = useSnackbar();
  const [selectedOption, setSelectedOption] = useState(
    optionsList.find(option => option.value === value)
  );

  useEffect(() => {
    const selectedItem = optionsList.find(option => option.value === value);
    if (selectedItem) {
      setSelectedOption(selectedItem);
    }
  }, [optionsList, value]);

  useEffect(() => {
    const fetchSelectedOption = async () => {
      if (!getDefaultSelectedOption || !value) return;
      try {
        const selectedValue = await getDefaultSelectedOption(value);
        if (selectedValue) {
          setSelectedOption(selectedValue);
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      }
    };
    if (!selectedOption && value && getDefaultSelectedOption) {
      fetchSelectedOption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, value]);

  let linkDropDownListItem: CustomPopOverListItemType[] = optionsList.map(
    option => {
      return {
        id: option.value,
        label: (
          <ListItemText
            // className={classes.listItemInChangeValueLink}
            classes={{
              root: classes.rootListItemPopOverLink,
              primary: classes.primaryListItemPopOverLink,
              secondary: classes.secondaryListItemPopOverLink
            }}
            primary={option.label}
            secondary={option.secondary_label}
          />
        )
      };
    }
  );
  if (linkDropDownListItem.length === 0) {
    linkDropDownListItem = [
      {
        id: 'no-items',
        label: (
          <ListItemText
            classes={{
              root: classes.rootListItemPopOverLink
            }}
            primary={t('COMMON.NO_ITEMS_LABEL')}
          />
        ),
        disabled: true
      }
    ];
  }
  return (
    <div className="update-value-link-wrapper">
      {selectedOption && (
        <div className="value">
          <ListItemText
            className={classes.listItem}
            primary={selectedOption.label}
            secondary={selectedOption.secondary_label}
          />
        </div>
      )}
      {!canUpdate && !selectedOption && <p>{t('COMMON.NONE_VALUE')}</p>}
      {canUpdate && (
        <div className="change-to-link-wrapper">
          <LinkDropdown
            title={value ? updateValueLabel : addValueLabel}
            position="right-start"
            listItems={linkDropDownListItem}
            handleItemClick={onSelectOption}
            enablePortal={true}
          />
        </div>
      )}
    </div>
  );
};

export default UpdateValueLink;
